import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { chain, groupBy } from "lodash";

import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  List,
  ListSubheader,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  BarChartOutlined,
  Delete,
  Domain,
  ExpandLess,
  ExpandMore,
  Output,
  PeopleAlt,
  Place,
  RestartAlt,
  Share,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";

import { ActionButtons, ButtonIcon, HelpBox, HelpText, MiniStatBox, SearchBox, TitleText } from "@/components/atoms";
import { PageContainer } from "@/components/containers";
import { SiteMap } from "@/components/molecules";
import { useAudienceContext } from "@/context";
import {
  useActionWorkflow,
  useAudience,
  useAudienceUtils,
  useSelectedSites,
  useServices,
  useSites,
} from "@/hooks";
import { Site } from "@/types";
import {
  abbreviateNumber,
  catchmentCategories,
  catchmentDayType, catchmentSiteType,
  createSiteDefinition,
  filterCatchment,
  filterCatchmentByCategory,
  filterCatchmentByDayType,
} from "@/utils";
import { SiteSkeleton } from "./SiteSkeleton";



const SitePage: React.FC = () => {

  const { ui } = useServices();
  const { state, dispatch } = useAudienceContext();

  // Fetch Audience
  let { audienceId } = useParams<{ audienceId: string }>();
  audienceId = audienceId === "new" ? undefined : audienceId;
  const {
    audience, loading: audLoad, error: audErr,
  } = useAudience(audienceId, "site");

  // Prepare Site Data
  const {
    sites, loading: siteLoad, error: siteErr
  } = useSites();

  // Parse selected sites from the audience
  const selectedSites = useSelectedSites(sites);

  // Site Selection variables
  const [searchText, setSearchText] = useState<string>("");
  const [siteType, setSiteType] = useState<string>("ALL");
  const [isExpanded, setExpanded] = useState<boolean>(false);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [openRegions, setOpenRegions] = useState<Record<string, boolean>>({});

  const { resetAction } = useActionWorkflow();
  const { utils, action } = useAudienceUtils(audienceId, audience);

  // Derive a single Page State
  const pageState = useMemo(() => {

    // Fetch status
    const isLoading = audLoad || siteLoad;
    const loadError = !!(audErr || siteErr);

    // Audience status
    const isNewAudience = audienceId === undefined;
    const isAudShared = !!audience?.shared;

    // Build status
    const isBuilt = !!(audience?.audience_size && audience?.audience_size.length > 0);
    const justRebuilt = (action.type === 'catchment' || action.type === 'build') && action.status === 'success';
    const audDefinition = createSiteDefinition(audience);
    const hasChanged = audDefinition !== state.currentDefinition;

    // Insight status
    const insightIsStale = hasChanged || justRebuilt;

    // Action status
    const isActioning = action.status === 'loading';
    const catchmentLoading = action.type === 'catchment' &&  action.status === 'loading'
    const sizeLoading = action.type === 'build' &&  action.status === 'loading'

    // Locked status
    const isLocked = isLoading || loadError || isAudShared

    return {
      isLoading,  // Data is loading
      isLocked,  // EditAccess is disabled
      isActioning, // Page is doing something
      isBuilt, // The audience has been built
      hasChanged, // Has the audience definition changed?
      insightIsStale, // Does the insight need regenerating?
      catchmentLoading, // Is the catchment endpoint loading?
      sizeLoading, // Is the audience size endpoint loading?

      errors: {
        AudienceNotFound: audErr,
        DataUnavailable: siteErr,
        SiteCatchmentError: (action.type === 'build' && action.status === 'error' && !hasChanged) && action.error,
      },

      can: {
        insight: isBuilt && !(isLoading || loadError || isActioning),
        export: isBuilt && !hasChanged && !(isLoading || loadError || isActioning),
        delete: isBuilt && audience && !isNewAudience && !isAudShared && !isLocked && !isActioning,
        share: isBuilt && !hasChanged && audience && !isNewAudience && !isAudShared && !isLocked && !isActioning,
      }
    }

  }, [state, action, audience, audienceId, audLoad, siteLoad, audErr, siteErr]);

  useEffect(() => {
    if (pageState.hasChanged) {
      resetAction();
    }
  }, [pageState.hasChanged, resetAction]);

  const filteredSites = useMemo(() => {
    let searchSites: Site[] = sites;
    if (searchText !== '') {
      searchSites = searchSites.filter(site => (
        site.site_name.toLowerCase().includes(searchText.toLowerCase()))
        || (site.landlord && site.landlord.toLowerCase().includes(searchText.toLowerCase()))
        || (site.region && site.region.toLowerCase().includes(searchText.toLowerCase()))
      )
    }
    if (siteType !== 'ALL') {
      searchSites = searchSites.filter(x => x.site_type === siteType)
    }
    return chain(searchSites)
      .groupBy('region')
      .map((value, key) => ({region: key, sites: value}))
      .orderBy(['region'], ['asc'])
      .value();

  }, [sites, searchText, siteType]);

  const filteredCatchment = useMemo(() => {
    const catchment = audience?.site_config.catchment;
    const categories = audience?.site_config.categories;
    const dayType = audience?.site_config.day_type;
    if (!catchment || !categories || catchment.length === 0) return [];

    return filterCatchment(catchment, categories, dayType)

  }, [audience?.site_config.catchment, audience?.site_config.categories, audience?.site_config.day_type])

  const handleSelectAll = () => {
    const updatedSites = [0];

    // Toggle the selectAll flag
    setSelectAll(true);

    // Get the site config
    const categories = audience?.site_config.categories || [];
    const dayType = audience?.site_config.day_type || 'ALL';

    // Update the site selection in the context
    dispatch({ type: 'SET_SITES', payload: updatedSites });

    // Run the catchment workflow
    utils.handleSiteCatchment(updatedSites, categories, dayType);
  }

  const handleSelectRegion = (e: React.MouseEvent, sites: Site[], remove: boolean ) => {
    e.stopPropagation();

    // Toggle the selectAll flag
    setSelectAll(false);

    // Update the site list
    let currentSites = audience?.site_config.sites || [];
    const isSelectAll = currentSites.length === 1 && currentSites[0] === 0
    const currentSiteIds = new Set<number>(isSelectAll ? [] : currentSites);

    if (remove) {
      for (const s of sites) {
        currentSiteIds.delete(s.site_id);
      }
    } else {
      for (const s of sites) {
        currentSiteIds.add(s.site_id);
      }
    }
    const updatedSites = Array.from(currentSiteIds);

    // Get the site config
    const categories = audience?.site_config.categories || [];
    const dayType = audience?.site_config.day_type || 'ALL';

    // Update the site selection in the context
    dispatch({ type: 'SET_SITES', payload: updatedSites });

    // Run the catchment workflow
    utils.handleSiteCatchment(updatedSites, categories, dayType);

  }

  const handleSelect = (site: Site) => {

    // Toggle the selectAll flag
    setSelectAll(false);

    // Update the site list
    let currentSites = audience?.site_config.sites || [];
    if ( currentSites.length === 1 && currentSites[0] === 0) {
      currentSites = [];
    }
    const exists = currentSites.some(s => s === site.site_id);
    const updatedSites = exists
      ? currentSites.filter(s => s !== site.site_id)
      : [...currentSites, site.site_id];

    // Get the site config
    const categories = audience?.site_config.categories || [];
    const dayType = audience?.site_config.day_type || 'ALL';

    // Update the site selection in the context
    dispatch({ type: 'SET_SITES', payload: updatedSites });

    // Run the catchment workflow
    utils.handleSiteCatchment(updatedSites, categories, dayType);

  }

  const handleRemove = (site: Site) => {
    const currentSites = audience?.site_config.sites || [];
    const updatedSites = currentSites.filter(s => s !== site.site_id);
    dispatch({ type: 'SET_SITES', payload: updatedSites })

    // Get the site config
    const categories = audience?.site_config.categories || [];
    const dayType = audience?.site_config.day_type || 'ALL';

    // Run the catchment workflow
    utils.handleSiteCatchment(updatedSites, categories, dayType);
  }

  const handleClear = () => {
    dispatch({type: 'SET_SITES', payload: []});

    // Run the catchment workflow
    utils.handleSiteCatchment([], [], 'ALL');
  }

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearchText(event.target.value || "");

  const handleSelectCategory = (category: string) => {

    // Update the categories
    const currentCategories = audience?.site_config.categories || [];
    const exists = currentCategories.some(c => c === category);
    const updatedCategories = exists
      ? currentCategories.filter(c => c !== category)
      : [...currentCategories, category];

    // Update the site categories in the context
    dispatch({ type: 'SET_SITE_CATEGORIES', payload: updatedCategories })

    // Load the catchment
    const catchment = audience?.site_config.catchment || [];

    // Filter the catchment to the categories
    const filteredCatchment = filterCatchmentByCategory(catchment, updatedCategories);

    // Run the build workflow
    if (filteredCatchment.length > 0) {
      utils.handleBuildSite(filteredCatchment.map(x => x.sector))
    } else {
      dispatch({ type: 'BUILD_AUDIENCE', payload: { audience_geo: 'sector', audience_size: undefined } });
    }
  };

  const handleSelectDayType = (dayType: string ) => {

    // Update the site day type in the context
    dispatch({ type: 'SET_SITE_DAY_TYPE', payload: dayType })

    // Load the catchment & categories
    const catchment = audience?.site_config.catchment || [];

    // Filter the catchment to the categories
    const filteredCatchment = filterCatchmentByDayType(catchment, dayType);

    // Run the build workflow
    if (filteredCatchment.length > 0) {
      utils.handleBuildSite(filteredCatchment.map(x => x.sector))
    } else {
      dispatch({ type: 'BUILD_AUDIENCE', payload: { audience_geo: 'sector', audience_size: undefined } });
    }
  };

  const clearText = () =>
    setSearchText("");

  const toggleRegion = (region: string) => {
    setOpenRegions((prev) => ({
      ...prev,
      [region]: prev[region] === undefined ? false : !prev[region],
    }));
  };

  const pageActions = useMemo(() => {

    const isBuilt = pageState.isBuilt;
    const hasChanged = pageState.hasChanged;
    const isComplete = audience?.status === 'complete';
    const isLoading = (type: string) => action.status === 'loading' && action.type === type;


    return [
      {
        id: 'delete',
        label: 'Delete',
        icon: Delete,
        onClick: () =>
          ui.openDialog({
            type: "deleteAudience",
            props: {
              audience,
              redirect: "/site",
            },
          }),
        disabled: !pageState.can.delete,
        isPrimary: false,
        color: 'error' as const
      },
      // {
      //   id: 'share',
      //   label: 'Share',
      //   icon: Share,
      //   onClick: () =>
      //     ui.openDialog({
      //       type: "shareAudience",
      //       props: {
      //         selectedEntity: audience,
      //         onShare: async (ids: number[]) => {
      //           await utils.handleShare(ids);
      //         },
      //       },
      //     }),
      //   disabled: !pageState.can.share,
      //   isPrimary: false,
      // },
      {
        id: 'insight',
        label: 'Insight',
        icon: BarChartOutlined,
        onClick: () => utils.handleInsight(pageState.insightIsStale),
        loading: isLoading('insight'),
        disabled: !pageState.can.insight,
        isPrimary: isBuilt,
      },
      {
        id: 'export',
        label: 'Export',
        icon: Output,
        onClick: utils.handleExport,
        disabled: !pageState.can.export,
        isPrimary: isBuilt && !hasChanged && isComplete,
      }
    ]
  }, [action, audience, pageState, ui, utils]);


  return (
    <PageContainer
      actions={<ActionButtons actions={pageActions} />}
      errors={pageState.errors}
      loading={action.status === 'loading' && action.type === 'insight'}
      loadingText={action.message}
    >
      {pageState.isLoading ?
        <SiteSkeleton />
      :
      <Grid container spacing={2} sx={{ pb: 4 }}>
        {audience && sites && (
          <>

            {/* Header */}
            <Grid
              size={12}
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography variant="h6">
                {audience.audience_name || "New Site Selection"}
              </Typography>
              {selectedSites && audience.audience_size ? (
                <Stack direction="row" spacing={2}>
                  <MiniStatBox
                    name="Sites Selected"
                    value={selectAll ? sites.length : selectedSites.length}
                    icon={<Domain fontSize="small" color="secondary" />}
                  />
                  <MiniStatBox
                    name="Catchment Sectors"
                    value={filteredCatchment.length}
                    icon={<Place fontSize="small" color="secondary" />}
                  />
                  <MiniStatBox
                    name="Exposed Audience"
                    value={audience.audience_size[0].population_count | 0}
                    icon={<PeopleAlt fontSize="small" color="secondary" />}
                    valueFormatter={abbreviateNumber}
                    loading={pageState.sizeLoading}
                  />
                </Stack>
              ) : (
                pageState.sizeLoading && (
                  <Stack direction="row" spacing={2}>
                    <Skeleton variant="rounded" width={120} height={50} />
                    <Skeleton variant="rounded" width={120} height={50} />
                    <Skeleton variant="rounded" width={120} height={50} />
                  </Stack>
                )
              )}
            </Grid>

            <Grid size={12}>
              <TitleText
                text="Select your Sites"
                subtext={`
                Select the sites you want to profile.
                Each site has a Catchment calculated by combining vehicle visits using ANPR, purchase data from Open Banking and movement tracking from Mobile Devices.
                `}
                variant="body1"
                spacing={0.5}
              />
            </Grid>

            {/* Site Selection */}
            <Grid size={4}>
              <Stack spacing={1}>
                <Paper
                  sx={{
                    height: 544,
                    overflowY: "hidden",
                    display: "flex",
                    flexDirection: "column"
                  }}>

                  {/* Search Box */}
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "6px 6px 0px 0px",
                      borderBottom: 1,
                      borderColor: "divider",
                      flexShrink: 0
                    }}
                  >
                    <Stack spacing={1} direction="row" sx={{ pb: 2 }}>
                      <SearchBox
                        value={searchText}
                        onSearch={handleSearchTextChange}
                        fullWidth={true}
                        placeholder="Search for a site"
                      />
                      {searchText !== "" && (
                        <ButtonIcon
                          onClick={clearText}
                          color="info"
                          size={40}
                          Icon={RestartAlt}
                          tooltip="Clear Search Text"
                        />
                      )}
                    </Stack>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Typography variant="caption">
                        Filter
                      </Typography>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                        {catchmentSiteType.map((option, idx) => {
                          const isSelected = siteType === option.name;
                          return (
                            <HelpBox title={option.description} key={idx}>
                              <Chip
                                key={option.name}
                                label={option.label}
                                size="small"
                                variant={isSelected ? "filled" : "outlined"}
                                color={isSelected ? "secondary" : "default"}
                                onClick={() => setSiteType(option.name)}
                                disabled={pageState.catchmentLoading}
                              />
                            </HelpBox>
                          )
                        })}
                      </Stack>
                    </Stack>
                  </Box>


                  {/* Selection Summary */}
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "6px 6px 0px 0px",
                      borderBottom: 1,
                      borderColor: "divider",
                      flexShrink: 0
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" color="secondary">
                          Selected {selectAll ? "ALL" : selectedSites.length}
                        </Typography>
                        {selectedSites.length > 0 && (
                          <HelpBox title="Clear all selected sites">
                            <IconButton
                              size="small"
                              onClick={() => handleClear()}
                            >
                              <RestartAlt fontSize="small" />
                            </IconButton>
                          </HelpBox>
                        )}
                      </Stack>
                      {selectedSites.length > 0 && (
                        <IconButton
                          size="small"
                          onClick={() => setExpanded((prev) => !prev)}
                        >
                          {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                      )}
                    </Stack>
                    <Collapse in={isExpanded}>
                      <Box
                        sx={{
                          maxHeight: 96,
                          overflowY: "auto",
                          mt: 1,
                          mr: -2,
                          // mb: -2,
                          // pb: 2
                        }}
                      >
                        {selectedSites.map((site, idx) => (
                          <Chip
                            key={idx}
                            label={site.site_name}
                            size="small"
                            variant="filled"
                            color="secondary"
                            onDelete={(e) => handleRemove(site) }
                            sx={{ m: 0.5 }}
                          />
                        ))}
                      </Box>
                    </Collapse>
                  </Box>

                  {/* Site List */}
                  <Box
                    sx={{
                      flexGrow: 1,
                      overflowY: "auto",
                      minHeight: 0
                    }}
                  >
                    <List dense sx={{ p: 1 }}>
                      {searchText === '' && siteType === 'ALL' && (
                        <ListItemButton
                          selected={selectAll}
                          onClick={() => handleSelectAll()}
                          sx={{ height: '50px', borderRadius: '6px', py: 0, mb: 1 }}
                        >
                          <ListItemText primary="ALL SITES"/>
                        </ListItemButton>
                      )}
                      {filteredSites && filteredSites.map(({ region, sites }) => {
                        const isOpen = openRegions[region] ?? true;
                        const selectedSiteIds = new Set(selectedSites.map(x => x.site_id));
                        const regionSites = sites.map(x => x.site_id);
                        const allSelected = regionSites.every(x => selectedSiteIds.has(x));

                        return (
                          <React.Fragment key={region}>

                            <ListSubheader
                              component="div"
                              onClick={() => toggleRegion(region)}
                              sx={{
                                backgroundColor: 'background.paper',
                                backgroundImage: "var(--Paper-overlay)",
                                // lineHeight: '16px',
                                // letterSpacing: '0.15px',
                                mt: 2,
                                mb: 1,
                                py: 0.5,
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <Box display='flex' flexDirection='row' alignItems='center'>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    textTransform: 'uppercase',
                                    fontFamily: 'poppins-semibold'
                                  }}
                                >
                                  {region} ({sites.length})
                                </Typography>
                                <Button
                                  size="small"
                                  variant="text"
                                  color="secondary"
                                  onClick={(e) => handleSelectRegion(e, sites, allSelected)}
                                  sx={{ fontSize: '10px', textTransform: 'none', mx: 1, minWidth: 'auto' }}
                                >
                                  {allSelected ? 'Deselect All' : 'Select All'}
                                </Button>
                              </Box>
                              <IconButton size="small">
                                {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                              </IconButton>
                            </ListSubheader>
                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                              <Box>
                                {sites.map((site) => (
                                  <ListItemButton
                                    key={site.site_id}
                                    selected={selectedSites.some(item => item.site_id === site.site_id)}
                                    onClick={() => handleSelect(site)}
                                    sx={{ borderRadius: '6px', py: 0, mb: 1 }}
                                  >
                                    <ListItemText
                                      primary={site.site_name}
                                      secondary={site.landlord}
                                      slotProps={{
                                        secondary: {
                                          fontSize: '12px'
                                        }
                                      }}
                                    />
                                  </ListItemButton>
                                ))}
                              </Box>
                            </Collapse>
                          </React.Fragment>
                        )

                      })}
                    </List>
                  </Box>
                </Paper>
              </Stack>
            </Grid>

            {/* Catchment Map */}
            <Grid size={8}>
              <SiteMap catchment={filteredCatchment} sites={selectedSites} selectAll={selectAll} loading={pageState.catchmentLoading}/>
            </Grid>

            <Grid size={12}>
              <TitleText
                text="Configure your Catchment"
                subtext={`
                Fine tune the catchment by selecting the type of visitors you want to profile and when they are likely to visit
                `}
                variant="body1"
                spacing={0.5}
              />
            </Grid>

            {/* Catchment Config */}
            <Grid>
              <Stack spacing={1.5} mr={2}>
                <HelpText
                  variant="caption"
                  text="Visitor Type (Select to include/exclude)"
                  maxWidth={400}
                  helpText={`
                  Postal Sectors in the Catchment have been classified into Visitor Types based on the frequency & volume 
                  of visits to each Site. 
                  `}
                />
                <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                  {catchmentCategories.map((category, idx) => {
                    const isSelected = audience.site_config.categories.includes(category.name);
                    const hexColor = ui.theme.palette.gradient[idx]
                    return (
                      <HelpBox title={category.description} key={idx}>
                        <Chip
                          key={category.name}
                          label={category.label}
                          size="small"
                          variant={isSelected ? "filled" : "outlined"}
                          sx={{
                            backgroundColor: isSelected ? hexColor : "transparent",
                            borderColor: isSelected ? "none" : hexColor,
                            '&:hover': {
                              backgroundColor: isSelected ? hexColor : `${hexColor}15`,
                              borderColor: hexColor,
                              opacity: 0.9,
                            },
                          }}
                          onClick={() => handleSelectCategory(category.name)}
                          disabled={pageState.catchmentLoading}
                        />
                      </HelpBox>
                    )
                  })}
                </Stack>
              </Stack>
            </Grid>

            {/* DOW Config */}
            <Grid>
              <Stack spacing={1.5}>
                <HelpText
                  variant="caption"
                  text="Day Type"
                  maxWidth={400}
                  helpText="Toggle between weekday or weekend journeys"
                />
                <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                  {catchmentDayType.map((option, idx) => {
                    const isSelected = audience.site_config.day_type === option.name;
                    return (
                      <HelpBox title={option.description} key={idx}>
                        <Chip
                          key={option.name}
                          label={option.label}
                          size="small"
                          variant={isSelected ? "filled" : "outlined"}
                          color={isSelected ? "secondary" : "default"}
                          onClick={() => handleSelectDayType(option.name)}
                          disabled={pageState.catchmentLoading}
                        />
                      </HelpBox>
                    )
                  })}
                </Stack>
              </Stack>
            </Grid>
          </>
        )}
      </Grid>
      }
    </PageContainer>
  );
};

export default SitePage;
